import { useEffect } from "react";

interface SavedBodyStyles {
  overflow: string;
  position: string;
  top: string;
  width: string;
  left: string;
}

let activeLocks = 0;
let savedScrollY = 0;
let savedStyles: SavedBodyStyles | null = null;

const lockBodyScroll = (): void => {
  if (activeLocks === 0) {
    savedScrollY = window.scrollY;
    const bodyStyle = document.body.style;
    savedStyles = {
      overflow: bodyStyle.overflow,
      position: bodyStyle.position,
      top: bodyStyle.top,
      width: bodyStyle.width,
      left: bodyStyle.left,
    };

    bodyStyle.overflow = "hidden";
    bodyStyle.position = "fixed";
    bodyStyle.top = `-${savedScrollY}px`;
    bodyStyle.width = "100%";
    bodyStyle.left = "0";
  }

  activeLocks += 1;
};

const unlockBodyScroll = (): void => {
  activeLocks = Math.max(0, activeLocks - 1);

  if (activeLocks !== 0 || !savedStyles) {
    return;
  }

  const bodyStyle = document.body.style;
  bodyStyle.overflow = savedStyles.overflow;
  bodyStyle.position = savedStyles.position;
  bodyStyle.top = savedStyles.top;
  bodyStyle.width = savedStyles.width;
  bodyStyle.left = savedStyles.left;

  window.scrollTo(0, savedScrollY);
  savedStyles = null;
  savedScrollY = 0;
};

/**
 * Locks document scrolling while preserving the exact page scroll position.
 *
 * This uses the iOS-safe `position: fixed; top: -scrollY` recipe instead of
 * only setting `overflow: hidden`, and it reference-counts nested surfaces so
 * closing an inner modal cannot unlock the page behind an outer modal.
 */
export const useBodyScrollLock = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled || typeof document === "undefined" || typeof window === "undefined") {
      return undefined;
    }

    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [enabled]);
};

export const __bodyScrollLockTestUtils = {
  getActiveLocks: (): number => activeLocks,
  resetForTests: (): void => {
    activeLocks = 0;
    savedScrollY = 0;
    savedStyles = null;
  },
};

export default useBodyScrollLock;

